import { createFileRoute } from "@tanstack/react-router";
import { LocationPageTemplate, type LocationVariant } from "@/components/LocationPageTemplate";
import { getLocationCity } from "@/data/location-cities";
import { canonical } from "@/lib/seo";

const PATH = "/senior-tech-support-montclair-ca";
const TITLE = "Senior Tech Support Montclair CA | SimpleTech Helper – (909) 255-9112";
const DESCRIPTION =
  "Need senior tech support in Montclair, CA? SimpleTech Helper provides patient, in-home and remote tech help for seniors. No jargon. Call (909) 255-9112.";

export const Route = createFileRoute("/senior-tech-support-montclair-ca")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "senior tech support Montclair CA, in-home tech help seniors Montclair, tech help for elderly Inland Empire, patient computer help seniors, (909) 255-9112",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonical(PATH) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "geo.region", content: "US-CA" },
      { name: "geo.placename", content: "Montclair" },
      { name: "geo.position", content: "34.0775;-117.6897" },
      { name: "ICBM", content: "34.0775, -117.6897" },
    ],
    links: [{ rel: "canonical", href: canonical(PATH) }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "SimpleTech Helper",
          address: {
            "@type": "PostalAddress",
            streetAddress: "8880 N Benson Ave #121",
            addressLocality: "Montclair",
            addressRegion: "CA",
            postalCode: "91763",
            addressCountry: "US",
          },
          telephone: "(909) 255-9112",
          url: "https://simpletechhelper.com",
          areaServed: "Montclair CA, Inland Empire",
          serviceType: "Senior Tech Support",
        }),
      },
    ],
  }),
  component: SeniorTechSupportMontclairPage,
});

const FAQS = [
  {
    q: "Do you come to the house in Montclair, or is it all over the phone?",
    a: "Both. SimpleTech Helper offers in-home visits to Montclair seniors as well as remote and phone-based help. We go with whatever your parent or loved one is most comfortable with.",
  },
  {
    q: "How much do you charge for senior tech help in Montclair?",
    a: "We charge flat rates by the hour or visit and tell you the price before we start. No surprise charges. Call (909) 255-9112 for current pricing.",
  },
  {
    q: "My parent feels embarrassed about how little they know. Will you be patient?",
    a: "Absolutely. We work at your pace, explain things as many times as needed, and never make anyone feel bad for asking questions. That's a promise we make to every Montclair senior we work with.",
  },
  {
    q: "My mom or dad in Montclair needs help but I live far away. Can I book for them?",
    a: "Yes. Many of our calls come from adult children arranging help for a parent in Montclair. You can book, pay, and be involved as much or as little as you'd like — we'll keep you updated.",
  },
  {
    q: "Can you help my parent in Montclair with scam calls, emails, and texts?",
    a: "Yes. We help Montclair seniors recognize and avoid phone scams, phishing emails, fake tech support pop-ups, and suspicious texts. We also help recover accounts that have already been compromised.",
  },
  {
    q: "My parent just got a new phone or computer. Can you help set it up in Montclair?",
    a: "Yes — this is one of our most common calls. We come to your Montclair home, set up the device, transfer contacts and photos, and walk your parent through the basics at their own pace.",
  },
  {
    q: "What if my parent has a question between visits?",
    a: "Monthly Check-In and Family Plan clients get free unlimited quick questions by phone or text between visits. For Single Visit clients, we're still just a call away at (909) 255-9112.",
  },
  {
    q: "Is SimpleTech Helper based near Montclair?",
    a: "Yes. Our office is at 8880 N Benson Ave #121, Montclair, CA 91763 — right in the heart of the Inland Empire. In-home visits to Montclair seniors happen fast because we're already local.",
  },
];

const INTRO = (
  <>
    <p>
      Technology should make life easier — not stressful.{" "}
      <strong className="text-[#0b0a3d]">SimpleTech Helper</strong> provides{" "}
      <strong className="text-[#0b0a3d]">senior tech support Montclair CA</strong> seniors and
      their families can count on: patient, in-home help for iPhones, laptops, email, Wi-Fi,
      printers, and more. We sit with you, explain things in plain English, and never rush.
    </p>
    <p>
      Our office is at{" "}
      <strong className="text-[#0b0a3d]">8880 N Benson Ave #121, Montclair, CA 91763</strong> — so we
      can be at your door quickly when you need us in person. Whether you want to FaceTime your
      grandkids, fix a slow computer, or just feel more confident with your devices, we're here.
    </p>
    <p>
      Ready for friendly, patient help? Call{" "}
      <a href="tel:+19092559112" className="text-sky-600 font-bold hover:underline whitespace-pre-line">
        {"\n"}(909) 255-9112
      </a>{" "}
      or schedule a free 15-minute call.
    </p>
  </>
);

const VARIANT: LocationVariant = {
  serviceLabel: "Senior Tech Support",
  serviceLabelShort: "Senior Tech Support",
  parentHref: "/services/tech-support-for-seniors",
  parentLabel: "Senior Tech Support",
  learnMoreText: "EXPLORE OUR SENIOR TECH SUPPORT SERVICES",
  nearMeEyebrow: "Senior Tech Help Near Me",
  whyTitle: "Why Families Trust SimpleTech Helper with Senior Tech Help in Montclair",
  expertHeroTitle:
    "Patient, Friendly Senior Tech Help in Montclair, CA",
  expertNearMeTitle: "Trusted Senior Tech Support in Montclair, CA",
  introStrong: "Senior Tech Support Montclair CA",
  introBody: INTRO,
  faqHeading: (
    <>Frequently Asked<br />Questions About<br />Senior Tech Help in Montclair</>
  ),
  ctaTitle: "Senior Tech Help in Montclair, CA – Friendly, Patient Computer Support",
  ctaBody: (
    <>
      <p className="mt-6 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
        Need reliable tech help from someone who understands seniors' technology needs? We provide patient, one-on-one computer and smartphone support for seniors in Montclair, CA. Whether you're struggling with email, Wi-Fi, smartphones, tablets, video calls, online safety, or computer issues, we're here to help—without the confusing technical jargon.
      </p>
      <p className="mt-6 text-xl md:text-2xl font-extrabold text-[#0b0a3d]">
        FREE 30-Minute Tech Consultation for Montclair Seniors
      </p>
      <p className="mt-4 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
        For a limited time, Montclair residents and local businesses can receive a <strong className="font-bold">FREE 30-minute technology consultation</strong> (a $199 value). Get expert guidance on computers, smartphones, internet connectivity, VoIP phone systems, cybersecurity, and more.
      </p>
      <p className="mt-6 text-slate-700 text-base md:text-lg">
        <strong className="font-bold">Call now:</strong>{" "}
        <a href="tel:+19092559112" className="text-sky-600 font-bold hover:underline whitespace-pre-line">
          {"\n"}(909) 255-9112
        </a>
      </p>
    </>
  ),
  blogEyebrow: "From the Blog",
  blogTitle: "Senior Tech Tips for Montclair Families",
  faqs: FAQS,
};

function SeniorTechSupportMontclairPage() {
  const city = getLocationCity("montclair")!;
  return <LocationPageTemplate city={city} variant={VARIANT} />;
}
